import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography, CircularProgress } from '@mui/material';
import {
  CheckCircle as AcceptIcon,
  ClearAll as ClearIcon,
  Edit as EditIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from '@mui/icons-material';

interface BulkActionToolbarProps {
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  onSelectAll: () => void;
  onBulkEdit: () => void;
  onBulkAccept: () => void;
  onClearSelection: () => void;
  isSubmitting?: boolean;
}

const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  totalCount,
  allSelected,
  onSelectAll,
  onBulkEdit,
  onBulkAccept,
  onClearSelection,
  isSubmitting = false,
}) => {
  const { t } = useTranslation('components');

  // Show toolbar when there are suggestions (for Select All) or when items are selected
  if (totalCount === 0) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        zIndex: 5,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Select All / Deselect All Button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={allSelected ? <DeselectIcon /> : <SelectAllIcon />}
        onClick={onSelectAll}
        disabled={isSubmitting}
      >
        {allSelected
          ? t('bulkActionToolbar.deselectAll', 'Deselect All')
          : t('bulkActionToolbar.selectAll', 'Select All')}
      </Button>

      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t('bulkActionToolbar.selected', { count: selectedCount })}
      </Typography>

      <Box sx={{ flexGrow: 1 }} />

      {/* Only show action buttons when items are selected */}
      {selectedCount > 0 && (
        <>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onBulkEdit}
            disabled={isSubmitting}
          >
            {t('bulkActionToolbar.editButton')}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={isSubmitting ? <CircularProgress size={18} /> : <AcceptIcon />}
            onClick={onBulkAccept}
            disabled={isSubmitting}
          >
            {t('bulkActionToolbar.acceptButton')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={onClearSelection}
            disabled={isSubmitting}
          >
            {t('bulkActionToolbar.clearButton')}
          </Button>
        </>
      )}
    </Paper>
  );
};

export default BulkActionToolbar;
